import { Grid } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { getObjetivos } from "../../controllers/operaciones";
import { useProyecto } from "../../data/ProyectoContext";
import { useAuthContext } from "../../hooks/Auth";
import { UseSetLenguajePorPais } from "../../hooks/UseSetLenguajePorPais";
import {
  iObjetivoAprendizaje,
  SelectedIndicator,
} from "../../interfaces/ObjetivosAprendizaje/iObjetivosAprendizaje";
import { useActividadesContext } from "../UmmIACategories/Hook/useActividades";
import SingleType from "./SingleType";
import LogoCardSkeleton from "../Loading/LogoCardSkeleton";

interface Props {
  nombreAsignatura: any;
  nombreNivel: any;
}


const ORG_FALLBACK = [{
  objetivo: "",
  tipo: "",
  indicadores: [] as string[],
}];

const CN_PROMPT_FALLBACK_ITEM = {
  objetivo: "",
  tipo: "",
  indicadores: [] as string[],
};

const TP_FALLBACK = [
  {
    objetivo: { id: "", codigo: "", texto: "" },
    aprendizajesEsperados: [
      {
        id: "",
        texto: "",
        criteriosEvaluacion: [
          { id: "", texto: "", objetivosGenericos: [{ id: "", texto: "" }] },
        ],
      },
    ],
  },
];

function normalizeIndicadores(value: any): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((x) => String(x ?? "")).filter(Boolean);
}

const GetObjetivosAprendizaje: FC<Props> = ({ nombreAsignatura, nombreNivel }) => {
  const [dataOa, setDataOa] = useState<iObjetivoAprendizaje[]>([]);
  const [selectedIndicators, setSelectedIndicators] =
    useState<SelectedIndicator>({});
  const [expandedPanel, setExpandedPanel] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { user } = useAuthContext();
  const { setPromptData, setObjetivosSeleccionados, setIsOAAdded } =
    useActividadesContext();
  const { proyecto, setProyecto } = useProyecto();

  UseSetLenguajePorPais();

  // Consulta de la API con asignatura y nivel
  useEffect(() => {
    if (!nombreAsignatura || !nombreNivel) return;

    async function fetchObjectives() {
      if (!user?.pais) return;
      setLoading(true);
      try {
        const res = await getObjetivos(user.pais, nombreAsignatura, nombreNivel);
        if (res.status === 200) {
          // Normaliza por seguridad 
          const raw = Array.isArray(res?.data?.body) ? res.data.body : [];
          const normalized = raw.map((row: any) => ({
            ...row,
            id_objetivo: String(row?.id_objetivo ?? ""),
            codigo: String(row?.codigo ?? ""),
            objetivo: String(row?.objetivo ?? row?.nombre ?? ""),
            nombre: String(row?.nombre ?? ""),
            tipo: String(row?.tipo ?? ""),
            asignatura: String(row?.asignatura ?? ""),
            nivel: String(row?.nivel ?? ""),
            indicadores: normalizeIndicadores(row?.indicadores),
          }));
          setDataOa(normalized);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    }

    fetchObjectives();
  }, [nombreAsignatura, nombreNivel, user]);

  // Sincronizar selectedIndicators con los indicadores persistidos en el proyecto 
  useEffect(() => {
    const cnList = Array.isArray((proyecto as any)?.objetivos_aprendizaje_cn)
      ? (proyecto as any).objetivos_aprendizaje_cn
      : [];

    const formattedObj = cnList.map((oa: any) => [
      oa.id_objetivo,
      Array.isArray(oa.indicadores) ? oa.indicadores : [],
    ]);

    setSelectedIndicators(Object.fromEntries(formattedObj));
  }, [proyecto]);

  // Manejo de selección de un indicador individual
  const handleIndicatorSelect = (idObjetivo: string, indicador: string) => {
    setSelectedIndicators((prev) => {
      const currentSelection = prev[idObjetivo] || [];
      if (currentSelection.includes(indicador)) {
        return {
          ...prev,
          [idObjetivo]: currentSelection.filter((item) => item !== indicador),
        };
      }
      return {
        ...prev,
        [idObjetivo]: [...currentSelection, indicador],
      };
    });
  };

  // Función para seleccionar o deseleccionar todos los indicadores de un objetivo
  const handleToggleAllIndicators = (idObjetivo: string, allIndicators: string[]) => {
    const current = selectedIndicators[idObjetivo] || [];
    const allSelected = current.length === allIndicators.length;

    setSelectedIndicators((prev) => ({
      ...prev,
      [idObjetivo]: allSelected ? [] : allIndicators,
    }));
  };

  // Agregar un objetivo al proyecto 
  const handleAddObjectiveToProject = (objetivo: iObjetivoAprendizaje) => {
    const idObjetivo = objetivo.id_objetivo;
    const selectedObjectiveIndicators = selectedIndicators[idObjetivo] || [];

    setProyecto((prev: any) => {
      const prevCn = Array.isArray(prev?.objetivos_aprendizaje_cn)
        ? prev.objetivos_aprendizaje_cn
        : [];

      const exists = prevCn.some((oa: any) => oa.id_objetivo === idObjetivo);
      if (exists) return prev;

      return {
        ...prev,


        objetivos_aprendizaje_cn: [
          ...prevCn,
          {
            id_objetivo: idObjetivo,
            codigo: (objetivo as any).codigo ?? "",
            objetivo: (objetivo as any).objetivo ?? (objetivo as any).nombre ?? "",
            indicadores: selectedObjectiveIndicators,
            tipo: (objetivo as any).tipo ?? "",
            materia: (objetivo as any).asignatura ?? "",
            grado: (objetivo as any).nivel ?? "",
          },
        ],


        objetivos_aprendizaje_org: prev?.objetivos_aprendizaje_org || ORG_FALLBACK,


        objetivos_aprendizaje_tp: prev?.objetivos_aprendizaje_tp || TP_FALLBACK,
      };
    });


    setPromptData((prevData: any) => {
      const prevCnPrompt = Array.isArray(prevData?.objetivos_aprendizaje_cn)
        ? prevData.objetivos_aprendizaje_cn
        : [];

      return {
        ...prevData,

        objetivos_aprendizaje_tp: prevData?.objetivos_aprendizaje_tp || TP_FALLBACK,


        objetivos_aprendizaje_cn: [
          ...prevCnPrompt,
          {
            objetivo: (objetivo as any).objetivo ?? (objetivo as any).nombre ?? "",
            tipo: (objetivo as any).tipo ?? "",
            indicadores: selectedObjectiveIndicators,
          },
        ],


        objetivos_aprendizaje_org: prevData?.objetivos_aprendizaje_org || ORG_FALLBACK,
      };
    });

    setObjetivosSeleccionados((prev) => ({ ...prev, [idObjetivo]: true }));
    setIsOAAdded(true);
  };


  const handleRemoveObjectiveFromProject = (objetivo: iObjetivoAprendizaje) => {
    const idObjetivo = objetivo.id_objetivo;

    setProyecto((prev: any) => {
      const prevCn = Array.isArray(prev?.objetivos_aprendizaje_cn)
        ? prev.objetivos_aprendizaje_cn
        : [];

      const updatedCn = prevCn.filter((oa: any) => oa.id_objetivo !== idObjetivo);
      if (updatedCn.length === 0) setIsOAAdded(false);

      return {
        ...prev,
        objetivos_aprendizaje_cn: updatedCn,
      };
    });

    // Actualizar objetivos seleccionados
    setObjetivosSeleccionados((prevSeleccionados) => {
      const updatedSeleccionados = { ...prevSeleccionados };
      delete updatedSeleccionados[idObjetivo];
      return updatedSeleccionados;
    });

    // Eliminar los indicadores relacionados
    setSelectedIndicators((prev) => {
      const updatedIndicators = { ...prev };
      delete updatedIndicators[idObjetivo];
      return updatedIndicators;
    });


    setPromptData((prevData: any) => {
      const prevCnPrompt = Array.isArray(prevData?.objetivos_aprendizaje_cn)
        ? prevData.objetivos_aprendizaje_cn
        : [];

      const textoOA =
        (objetivo as any).objetivo ?? (objetivo as any).nombre ?? "";

      const updatedCnPrompt = prevCnPrompt.filter((o: any) => o.objetivo !== textoOA);

      return {
        ...prevData,
        objetivos_aprendizaje_cn: updatedCnPrompt,
      };
    });
  };

  const orderedTypes = useMemo(
    () => [...new Set(dataOa.map((item: any) => item.tipo))].sort(),
    [dataOa]
  );

  return (
    <>
      {loading ? (
        <Grid display={"flex"} justifyContent={"center"}>

          <LogoCardSkeleton />
          
        </Grid>
      ) : (
        <Grid container spacing={4} m={4} mt={-5} maxWidth={"90%"}>
          {orderedTypes.map((category) => (
            <SingleType
              key={category}
              handleIndicatorSelect={handleIndicatorSelect}
              selectedIndicators={selectedIndicators}
              handleAddObjectiveToProject={handleAddObjectiveToProject}
              handleToggleAllIndicators={handleToggleAllIndicators}
              handleRemoveObjectiveFromProject={handleRemoveObjectiveFromProject}
              expandedPanel={expandedPanel}
              setExpandedPanel={setExpandedPanel}
              category={category}
              dataOa={dataOa}
              setSelectedIndicators={setSelectedIndicators}
            />
          ))}
        </Grid>
      )}
    </>
  );
};

export default GetObjetivosAprendizaje;
